import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AddTeamMemberInput, TeamInviteItem, TeamMemberItem, TeamSettingsResponse } from "@unbox/api";
import { handleError } from "@/api/error";
import { useMerchant } from "@/providers/MerchantProvider";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export type { AddTeamMemberInput, TeamInviteItem, TeamMemberItem, TeamSettingsResponse };

/** Role assignable on a single brand (UNB-1956) — `workspace_admin` is workspace-wide. */
export type TeamRole = AddTeamMemberInput["role"];

const teamKey = (merchantId: string, workspaceId: string | null) => ["team", merchantId, workspaceId];

/** Members and outstanding invites for the brand in scope. */
export const useTeam = () => {
  const scope = useScopedMerchantClient();

  return useQuery<TeamSettingsResponse>({
    queryKey: teamKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].team.$get({ param: scope.param })
        : await client.merchant[":merchantId"].team.$get({ param: scope.param });

      if (!res.ok) throw await handleError(res);
      return (await res.json()) as TeamSettingsResponse;
    },
    enabled: !!scope.merchantId,
  });
};

/** Invalidates the aggregated Team read after any write. */
const useInvalidateTeam = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: teamKey(scope.merchantId, scope.workspaceId) });
};

/**
 * Add someone to the brand. The API invites a new email and grants access
 * directly to an account that already exists, so callers don't have to know
 * which case they're in.
 */
export const useAddTeamMember = () => {
  const scope = useScopedMerchantClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (json: AddTeamMemberInput) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].team.$post({ param: scope.param, json })
        : await client.merchant[":merchantId"].team.$post({ param: scope.param, json });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useUpdateTeamMemberRole = () => {
  const scope = useScopedMerchantClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: TeamRole }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].team.members[":userId"].$patch({
            param: { ...scope.param, userId },
            json: { role },
          })
        : await client.merchant[":merchantId"].team.members[":userId"].$patch({
            param: { ...scope.param, userId },
            json: { role },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useRemoveTeamMember = () => {
  const scope = useScopedMerchantClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].team.members[":userId"].$delete({
            param: { ...scope.param, userId },
          })
        : await client.merchant[":merchantId"].team.members[":userId"].$delete({
            param: { ...scope.param, userId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

/** A pending invite carries its role on the invitation, not on a membership row. */
export const useUpdateTeamInviteRole = () => {
  const scope = useScopedMerchantClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async ({ inviteId, role }: { inviteId: string; role: TeamRole }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].team.invites[":id"].$patch({
            param: { ...scope.param, id: inviteId },
            json: { role },
          })
        : await client.merchant[":merchantId"].team.invites[":id"].$patch({
            param: { ...scope.param, id: inviteId },
            json: { role },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useResendTeamInvite = () => {
  const scope = useScopedMerchantClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].team.invites[":id"].resend.$post({
            param: { ...scope.param, id },
          })
        : await client.merchant[":merchantId"].team.invites[":id"].resend.$post({ param: { ...scope.param, id } });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

export const useRevokeTeamInvite = () => {
  const scope = useScopedMerchantClient();
  const invalidate = useInvalidateTeam();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].team.invites[":id"].revoke.$post({
            param: { ...scope.param, id },
          })
        : await client.merchant[":merchantId"].team.invites[":id"].revoke.$post({ param: { ...scope.param, id } });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: invalidate,
  });
};

/** People who can configure safety for the brand in scope: its merchant admins and workspace admins. */
export const useMerchantAdmins = () => {
  const merchantId = useMerchant();
  const team = useTeam();
  const admins = (team.data?.members ?? []).filter(
    (member) =>
      member.isWorkspaceAdmin ||
      member.brands.some((brand) => brand.merchantId === merchantId && brand.role === "merchant_admin"),
  );
  return { ...team, admins };
};
